<template>
  <el-dialog
    title="订单退款记录"
    :visible.sync="dialogVisible"
    width="800px"
    :before-close="handleClose">
    
    <div class="refund-log-container">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
        <span>加载中...</span>
      </div>
      
      <!-- 退款记录列表 -->
      <div v-else-if="refundLogs.length > 0" class="refund-log-list">
        <div 
          v-for="log in refundLogs" 
          :key="log.id" 
          class="refund-log-item">
          
          <div class="log-header">
            <div class="log-sn">
              <span class="label">退款单号:</span>
              <span class="value copyable" @click="copyToClipboard(log.sn, '退款单号')">
                {{ log.sn }}
              </span>
              <i class="el-icon-copy-document copy-icon" @click="copyToClipboard(log.sn, '退款单号')"></i>
            </div>
            <div class="log-status">
              <el-tag 
                :type="getStatusType(log.status)" 
                size="small">
                {{ getStatusText(log.status) }}
              </el-tag>
            </div>
          </div>
          
          <div class="log-content">
            <div class="log-item">
              <span class="label">退款金额:</span>
              <span class="value amount">{{ log.cn_money }} 元</span>
            </div>
            <div class="log-item">
              <span class="label">退款方式:</span>
              <span class="value">{{ getPaymentMethod(log.pla) }}</span>
            </div>
            <div class="log-item">
              <span class="label">创建时间:</span>
              <span class="value">{{ log.created_at }}</span>
            </div>
            <div class="log-item">
              <span class="label">更新时间:</span>
              <span class="value">{{ log.updated_at }}</span>
            </div>
            <div class="log-item" v-if="log.note">
              <span class="label">备注:</span>
              <span class="value">{{ log.note }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 无数据状态 -->
      <div v-else class="no-data">
        <el-empty description="暂无退款记录"></el-empty>
      </div>
    </div>
    
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
import axios from 'axios'

export default {
  name: 'RefundLogDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      refundLogs: []
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.orderId) {
        this.loadRefundLogs()
      }
    }
  },
  methods: {
    // 加载退款记录
    async loadRefundLogs() {
      if (!this.orderId) {
        this.$message.error('订单ID不能为空')
        return
      }
      
      this.loading = true
      try {
        const response = await axios.get(`${this.base_url}/orders/back_log/${this.orderId}`)
        if (response.status === 200 && response.data.code === 1) {
          this.refundLogs = response.data.data || []
        } else {
          this.$message.error(response.data.message || '获取退款记录失败')
          this.refundLogs = []
        }
      } catch (error) {
        console.error('获取退款记录失败:', error)
        this.$message.error('获取退款记录失败')
        this.refundLogs = []
      } finally {
        this.loading = false
      }
    },
    
    // 获取状态类型
    getStatusType(status) {
      const typeMap = {
        0: 'info',
        1: 'success',
        2: 'warning',
        3: 'danger'
      }
      return typeMap[status] || 'info'
    },
    
    // 获取状态文本
    getStatusText(status) {
      const textMap = {
        0: '待处理',
        1: '已完成',
        2: '处理中',
        3: '已失败'
      }
      return textMap[status] || '未知状态'
    },
    
    // 获取支付方式
    getPaymentMethod(pla) {
      const methodMap = {
        'wxpay': '微信支付',
        'alipay': '支付宝',
        'bank': '银行卡',
        'cash': '现金',
        'other': '其他'
      }
      return methodMap[pla] || pla || '未知'
    },
    
    // 复制到剪贴板
    async copyToClipboard(text, label) {
      if (!text || text === '-') {
        this.$message.warning(`${label}信息为空，无法复制`)
        return
      }
      
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = text
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          textArea.style.top = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          document.execCommand('copy')
          textArea.remove()
        }
        this.$message.success(`${label}已复制到剪贴板`)
      } catch (err) {
        console.error('复制失败:', err)
        this.$message.error('复制失败，请手动复制')
      }
    },
    
    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false
      this.refundLogs = []
    }
  }
}
</script>

<style lang="scss" scoped>
.refund-log-container {
  min-height: 200px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
  
  .el-icon-loading {
    font-size: 24px;
    margin-bottom: 10px;
  }
}

.refund-log-list {
  max-height: 500px;
  overflow-y: auto;
}

.refund-log-item {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  margin-bottom: 15px;
  padding: 15px;
  background: #fafafa;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.log-sn {
  display: flex;
  align-items: center;
  
  .label {
    font-weight: 500;
    color: #606266;
    margin-right: 8px;
  }
  
  .value {
    color: #409eff;
    font-family: monospace;
    
    &.copyable {
      cursor: pointer;
      padding: 2px 4px;
      border-radius: 3px;
      transition: all 0.2s;
      
      &:hover {
        background: #ecf5ff;
        transform: translateY(-1px);
      }
    }
  }
  
  .copy-icon {
    margin-left: 6px;
    color: #909399;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    
    &:hover {
      color: #409eff;
      transform: scale(1.1);
    }
  }
}

.log-status {
  .el-tag {
    font-weight: 500;
  }
}

.log-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.log-item {
  display: flex;
  align-items: center;
  
  .label {
    font-weight: 500;
    color: #606266;
    margin-right: 8px;
    min-width: 80px;
  }
  
  .value {
    color: #303133;
    
    &.amount {
      color: #f56c6c;
      font-weight: 600;
    }
  }
}

.no-data {
  padding: 40px;
  text-align: center;
}

.dialog-footer {
  text-align: right;
}
</style>